import { useEffect, useState } from "react";
import {
  Box,
  Pagination,
  Stack,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useFetchContext } from "../../../contexts/FetchContext";
import { IDataPagination } from "../../../interfaces";
import { PageContainer } from "../../shared/components/PageContainer";

import NoResult from "../../shared/components/NoResult";
import TableFilter from "../../shared/components/TableFilter";
import DownloadIcon from "@mui/icons-material/Download";
import MovementLogTable from "./MovementLogTable";
import {
  DocumentsData,
  DocumentStatus,
  DocumentType,
} from "../../../interfaces/document";
import MovementFilterContent from "./MovementFilterContent";
import moment from "moment";

export const MovementLog = () => {
  const { state, fetchData } = useFetchContext();

  const [order, setOrder] = useState<"ASC" | "DESC">("ASC");
  const [orderType, setOrderType] = useState<string>("createdAt");
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
    undefined
  );

  const handleSortChange = (state: "ASC" | "DESC") => {
    setOrder(state);
  };

  const handleOrderTypeChange = (type: string) => {
    setOrderType(type);
  };

  const handleSearchChange = (searchTerm: string) => {
    setSearchKeyword(searchTerm);
  };

  const [data, setData] = useState<IDataPagination<DocumentsData> | null>(null);
  const [page, setPage] = useState(1);

  const currentYear = new Date().getFullYear();
  const initStartDate = new Date(currentYear, 0, 1).toISOString();

  const [startDate, setStartDate] = useState<Date | null | String>(
    initStartDate
  );

  const [endDate, setEndDate] = useState<Date | null | String>(null);

  const [type, setType] = useState<DocumentType | string | null>(null);

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const response = await fetchData<IDataPagination<DocumentsData>>(
          `/documents?status=${DocumentStatus.CLOSED}&page=${page}&pageSize=10${
            searchKeyword ? `&searchKeyword=${searchKeyword}` : ""
          }${type ? `&type=${type}` : ""}${
            endDate ? `&endUpdatedAt=${endDate}` : ""
          }&orderType=${order}&orderBy=${orderType}&startUpdatedAt=${startDate}`
        );

        setData(response?.data);
      } catch (error) {}
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchKeyword, order, orderType, startDate, endDate, type]);

  return (
    <PageContainer>
      <Stack>
        <Typography variant="h6" mb={3} mt={1} align="center">
          TOTALE MOVIMENTAZIONE: {data?.total ?? 0}
        </Typography>
        <Grid container>
          <Grid item xs={10} sx={{ pb: 2 }}>
            <TableFilter
              sortOrder={order}
              onSearchChange={handleSearchChange}
              searchKeyword={searchKeyword}
              filterContent={
                <MovementFilterContent
                  onApplyFilter={(state, startDate, endDate) => {
                    setType(state);

                    const finalStartDate = moment(
                      new Date(startDate ?? initStartDate)
                    ).format();

                    const finalEndDate = endDate
                      ? moment(new Date(endDate)).format()
                      : null;

                    setStartDate(finalStartDate);
                    setEndDate(finalEndDate);
                    setPage(1);
                  }}
                  status={null}
                  date={null}
                />
              }
            />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{ pb: 2, display: "flex", justifyContent: "center" }}
          >
            <IconButton
              color="primary"
              onClick={async () => {
                try {
                  const response = await fetchData<any>(
                    "/documents/movement-log/xls",
                    "POST",
                    null,
                    undefined,
                    {
                      responseType: "blob",
                      headers: {
                        Accept:
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      },
                    }
                  );

                  const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = window.URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.download = "movement-log.xlsx";
                  document.body.appendChild(link);
                  link.click();

                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error("Errore durante il download:", error);
                }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Grid>

          {state.isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : data?.total ? (
            <MovementLogTable
              data={data?.items ?? []}
              order={order}
              orderType={orderType}
              handeSortChange={handleSortChange}
              handleOrderTypeChange={handleOrderTypeChange}
            />
          ) : (
            <NoResult />
          )}
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 15,
            mt: 5,
          }}
        >
          <Pagination
            count={data?.totalPages ?? 0}
            size="small"
            onChange={(e, page) => setPage(page)}
            page={page}
          />
        </Box>
      </Stack>
    </PageContainer>
  );
};

export default MovementLog;
